import { useEffect, useRef, useState, type ReactNode } from "react";

import { Tooltip, Typography, type TypographyProps } from "@mui/material";

export interface TruncatedTextProps extends Omit<TypographyProps, "noWrap"> {
  /**
   * Tooltip content, shown only when the text is actually truncated. Defaults
   * to `children` (use this when the visible text differs from the full value).
   */
  tooltip?: ReactNode;
}

/**
 * Single-line text that ellipsis-truncates when it overflows and reveals the
 * full content in a tooltip on hover — but only while it is actually truncated.
 * A short value that fits shows no tooltip. Accepts all Typography props.
 */
export function TruncatedText({
  tooltip,
  children,
  ...typographyProps
}: TruncatedTextProps) {
  const ref = useRef<HTMLElement>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const check = () => setTruncated(element.scrollWidth > element.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children]);

  return (
    // An empty title renders no tooltip, so it appears only once truncated.
    <Tooltip title={truncated ? (tooltip ?? children) : ""}>
      <Typography ref={ref} noWrap {...typographyProps}>
        {children}
      </Typography>
    </Tooltip>
  );
}
